import type { ForceDirection, ForceType } from "@/lib/types";

const FORCE_COLORS: Record<ForceType, string> = {
	gravity: "#e74c3c",
	normal: "#2ecc71",
	friction: "#f39c12",
	tension: "#9b59b6",
	applied: "#3498db",
	"air-resistance": "#1abc9c",
	spring: "#e67e22",
	"weight-component-parallel": "#c0392b",
	"weight-component-perpendicular": "#d35400",
};

const FORCE_LABELS: Record<ForceType, string> = {
	gravity: "Fg",
	normal: "FN",
	friction: "Ff",
	tension: "FT",
	applied: "Fapp",
	"air-resistance": "Fair",
	spring: "Fsp",
	"weight-component-parallel": "Fg\u2225",
	"weight-component-perpendicular": "Fg\u22A5",
};

const DIR_VECTORS: Record<ForceDirection, [number, number]> = {
	up: [0, -1],
	down: [0, 1],
	left: [-1, 0],
	right: [1, 0],
	"up-left": [-0.707, -0.707],
	"up-right": [0.707, -0.707],
	"down-left": [-0.707, 0.707],
	"down-right": [0.707, 0.707],
};

interface ForceArrowProps {
	cx: number;
	cy: number;
	type: ForceType;
	direction: ForceDirection;
	magnitude: "small" | "medium" | "large";
	label?: string;
	isGhost?: boolean;
	onRemove?: () => void;
}

export function ForceArrow({ cx, cy, type, direction, magnitude, label, isGhost, onRemove }: ForceArrowProps) {
	const color = FORCE_COLORS[type];
	const displayLabel = label ?? FORCE_LABELS[type];
	const [dx, dy] = DIR_VECTORS[direction];
	const lengths = { small: 45, medium: 65, large: 85 };
	const len = lengths[magnitude];

	const endX = cx + dx * len;
	const endY = cy + dy * len;

	// Arrowhead
	const headLen = 10;
	const angle = Math.atan2(dy, dx);
	const a1x = endX - headLen * Math.cos(angle - 0.4);
	const a1y = endY - headLen * Math.sin(angle - 0.4);
	const a2x = endX - headLen * Math.cos(angle + 0.4);
	const a2y = endY - headLen * Math.sin(angle + 0.4);

	// Label position
	const labelX = endX + dx * 16;
	const labelY = endY + dy * 16;

	return (
		<g style={{ cursor: onRemove ? "pointer" : "default" }} onClick={onRemove} opacity={isGhost ? 0.35 : 1}>
			{/* Thicker clickable area */}
			{onRemove && (
				<line x1={cx} y1={cy} x2={endX} y2={endY} stroke="transparent" strokeWidth={14} />
			)}
			{/* Arrow shaft */}
			<line x1={cx} y1={cy} x2={endX} y2={endY} stroke={color} strokeWidth={3} strokeLinecap="round" />
			{/* Arrowhead */}
			<polygon points={`${endX},${endY} ${a1x},${a1y} ${a2x},${a2y}`} fill={color} />
			{/* Label */}
			<text
				x={labelX}
				y={labelY}
				fill={color}
				fontSize={13}
				fontWeight={700}
				textAnchor="middle"
				dominantBaseline="middle"
				style={{ userSelect: "none", fontFamily: "system-ui, sans-serif" }}
			>
				{displayLabel}
			</text>
			{/* Remove indicator */}
			{onRemove && !isGhost && (
				<circle cx={endX} cy={endY} r={6} fill="hsl(var(--destructive))" opacity={0} className="group-hover:opacity-100">
					<title>Click to remove</title>
				</circle>
			)}
		</g>
	);
}

export { FORCE_COLORS, FORCE_LABELS, DIR_VECTORS };
